import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/cn';

export type ButtonVariant = 'primary' | 'gold' | 'secondary' | 'ghost' | 'outline' | 'danger';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-gradient-to-b from-agave-400 to-agave-600 text-ink-1000 font-semibold shadow-[0_10px_30px_-12px_rgba(0,201,140,0.8)] hover:from-agave-300 hover:to-agave-500 active:from-agave-500 active:to-agave-700',
  gold: 'bg-gradient-to-b from-gold-300 to-gold-600 text-ink-1000 font-semibold shadow-[0_10px_30px_-12px_rgba(249,160,7,0.8)] hover:from-gold-200 hover:to-gold-500',
  secondary:
    'glass text-ink-100 font-medium hover:border-agave-400/50 hover:text-white hover:bg-ink-850/70',
  ghost: 'text-ink-200 hover:text-white hover:bg-white/6',
  outline:
    'border border-ink-700 text-ink-100 font-medium hover:border-agave-400/60 hover:text-white',
  danger:
    'bg-gradient-to-b from-crimson-500 to-crimson-600 text-white font-semibold shadow-[0_10px_30px_-12px_rgba(217,45,76,0.8)] hover:brightness-110',
};

const SIZES: Record<ButtonSize, string> = {
  xs: 'h-7 px-2.5 text-xs gap-1 rounded-lg',
  sm: 'h-9 px-3.5 text-sm gap-1.5 rounded-lg',
  md: 'h-11 px-5 text-sm gap-2 rounded-xl',
  lg: 'h-13 px-7 text-base gap-2 rounded-xl',
  xl: 'h-16 px-9 text-lg gap-2.5 rounded-2xl',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  fullWidth?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  iconLeft,
  iconRight,
  fullWidth,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={cn(
        'relative inline-flex select-none items-center justify-center whitespace-nowrap transition-all duration-200',
        'disabled:pointer-events-none disabled:opacity-45',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : iconLeft}
      {children}
      {!loading && iconRight}
    </button>
  );
}
